import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ModalConfirm } from "tamtam-components";
import Select from "react-select";
import { toast } from "react-toastify";

import Loader from "../../common/Loader";
import Button from "../../common/Button";
import FormInput from "../../common/FormInput";
import _ from "../../../i18n";
import {
  saveGoPeopleClient,
  saveGoPeopleContact,
  updateGoPeopleClient,
  getGoPeopleFoldersByUen,
  saveGoPeopleFolderContact,
  searchGoPeopleCommunityByEntity,
} from "../../../api";
import { LANGUAGES, LEGAL_FORM, SELECT_STYLES } from "../../../config";
import {
  validateEmail,
  validateUen,
  formatPhone,
  formatHidePhone,
  obscureEmail,
  parseApiError,
} from "../../../utils";
import styles from "./Client.module.scss";

let isFetchingEmail = false;

// Same pattern as the collaborator form, itself copied from the gopeople phone
// validation tag, so both forms accept exactly what the backend accepts.
const PHONE_REGEX = /^\+?[0-9\s\-()]{8,25}$/;

// The API reports validation errors on the user fields, which this form holds
// under its own linked-contact keys, so the canonical names returned by
// parseApiError are translated here.
const CONTACT_FIELD_KEYS = {
  firstname: "contactFirstName",
  lastname: "contactLastName",
  phone: "contactPhone",
  email: "contactEmail",
};

const ClientForm = (props) => {
  const auth = useSelector((state) => state.auth);
  const [isSaving, setIsSaving] = useState(false);
  const [isFetchingUser, setIsFetchingUser] = useState(false);
  const [isFetchingCompany, setIsFetchingCompany] = useState(false);
  const [isValidForm, setIsValidForm] = useState(false);
  const [fetchedFolder, setFetchedFolder] = useState(null);
  const [showConfirmFolder, setShowConfirmFolder] = useState(false);
  const [disableFields, setDisableFields] = useState(false);
  const [disableContactFields, setDisableContactFields] = useState(false);
  const { goPeopleCommunityId, jwtToken, goPeopleOrganizationId } = auth;
  let saveInterval;

  const initErrors = {
    uen: "",
    name: "",
    legalForm: "",
    languagePreferences: "",
    contactEmail: "",
    contactFirstName: "",
    contactLastName: "",
    contactPhone: "",
  };
  const initData = {
    id: "",
    uen: "",
    name: "",
    legalForm: { value: "SRL", label: LEGAL_FORM.SRL },
    languagePreferences: [LANGUAGES[0]],
    contactId: "",
    contactEmailId: "",
    contactEmail: "",
    contactEmailValue: "",
    contactFirstName: "",
    contactLastName: "",
    contactPhone: "",
    contactPhoneId: "",
    contactPhoneValue: "",
  };
  const [errors, setErrors] = useState(initErrors);
  const [data, setData] = useState(initData);

  useEffect(() => {
    if (props.currentItem) {
      let languagePreferences = [];
      if (
        props.currentItem.languages &&
        props.currentItem.languages.length > 0
      ) {
        props.currentItem.languages.forEach((lng) => {
          LANGUAGES.forEach((i) => {
            if (i.value === lng) {
              languagePreferences.push(i);
            }
          });
        });
      }

      let legalRepresentative = props.currentItem.contacts.filter(
        (c) => c.role_type === "LEGAL_REPRESENTATIVE"
      )[0];
      setData({
        id: props.currentItem.id,
        uuid: props.currentItem.uuid,
        uen: props.currentItem.uen,
        name: props.currentItem.folder_name,
        legalForm: {
          value: props.currentItem.legal_form,
          label: LEGAL_FORM[props.currentItem.legal_form],
        },
        languagePreferences: languagePreferences ? languagePreferences : "",
        contactId: legalRepresentative?.contact_id,
        contactEmail: legalRepresentative?.emails?.length
          ? legalRepresentative?.emails[0]
          : "",
        contactEmailValue: legalRepresentative?.emails?.length
          ? legalRepresentative?.emails[0]
          : "",
        contactFirstName: legalRepresentative
          ? legalRepresentative.first_name
          : "",
        contactLastName: legalRepresentative
          ? legalRepresentative.last_name
          : "",
        contactPhone: legalRepresentative?.phones?.length
          ? legalRepresentative?.phones[0]
          : "",
        contactPhoneValue: legalRepresentative?.phones?.length
          ? legalRepresentative?.phones[0]
          : "",
      });
      setDisableFields(false);
    }
  }, [props.currentItem]);

  // The linked contact is required when creating a client, not when editing one: a
  // folder that has no legal representative hydrates those three fields to "", and
  // requiring them unconditionally would make such a record impossible to edit.
  const isCreation = !props.currentItem;

  useEffect(() => {
    // Emptiness has to be checked here, not only through errors: those are set by
    // handleKeyUp, so a field the user never touched stays error free and the submit
    // used to light up with three required fields still blank.
    const missingContact =
      isCreation &&
      (data.contactEmail?.length === 0 ||
        data.contactLastName?.length === 0 ||
        data.contactFirstName?.length === 0);

    setIsValidForm(
      data.uen?.length === 0 ||
        data.name?.length === 0 ||
        missingContact ||
        errors.uen ||
        errors.name ||
        errors.legalForm ||
        errors.contactEmail ||
        errors.contactFirstName ||
        errors.contactLastName ||
        // The phone stays optional, but a malformed one must not be submittable.
        errors.contactPhone
        ? false
        : true
    );
  }, [
    data.uen,
    data.name,
    data.contactEmail,
    data.contactFirstName,
    data.contactLastName,
    isCreation,
    errors.uen,
    errors.name,
    errors.legalForm,
    errors.languagePreferences,
    errors.contactEmail,
    errors.contactFirstName,
    errors.contactLastName,
    errors.contactPhone,
  ]);

  // Pure so that validate() can decide on freshly computed values instead of reading
  // the errors state, which is one render behind.
  const getFieldError = (fieldName, fieldValue) => {
    const value = fieldValue ?? "";
    if (value.length === 0) {
      return fieldName === "contactPhone" ? "" : _("required_field");
    }
    switch (fieldName) {
      case "uen":
        return validateUen(value) ? "" : _("validate_uen");
      case "name":
      case "contactLastName":
      case "contactFirstName":
        return value.length < 2 ? _("required_2_characters") : "";
      case "contactEmail":
        return validateEmail(value) ? "" : _("validate_email");
      case "contactPhone":
        // Was a bare minimum length, so letters went through and there was no
        // upper bound. Same rule as the collaborator form, itself aligned on the
        // gopeople phone pattern. Not trimmed: the character class already covers
        // the whitespace and parentheses formatPhone introduces.
        return PHONE_REGEX.test(value) ? "" : _("validate_phone");
      default:
        return "";
    }
  };

  const handleKeyUp = (fieldName, fieldValue) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: getFieldError(fieldName, fieldValue),
    }));
  };

  // Second guard, at submit time: the form had none at all, so a click was enough to
  // send an incomplete payload whatever the button state. contactPhone is checked
  // too, without becoming mandatory: getFieldError returns "" for an empty phone,
  // so only a filled and malformed value blocks the submit.
  const CREATION_VALIDATED_FIELDS = [
    "uen",
    "name",
    "contactEmail",
    "contactLastName",
    "contactFirstName",
    "contactPhone",
  ];

  const validate = () => {
    const newErrors = { ...errors };
    CREATION_VALIDATED_FIELDS.forEach((field) => {
      newErrors[field] = getFieldError(field, data[field]);
    });
    setErrors(newErrors);

    return CREATION_VALIDATED_FIELDS.some((field) => newErrors[field]);
  };

  const handleEmailChange = (name, value) => {
    if (value.length === 0)
      setErrors({ ...errors, [name]: _("required_field") });
    else if (value.length > 0 && !validateEmail(value)) {
      setErrors({ ...errors, email: _("validate_email") });
    }
    setData({ ...data, [name]: value, contactEmailValue: value });
  };

  // Every path out of a save has to reach one of these two, otherwise the button keeps
  // spinning forever and the list is never refreshed.
  const finishSave = () => {
    setIsSaving(false);
    clearInterval(saveInterval);
    props.afterSave();
    setData(initData);
    props.setCurrentItem(null);
    toast.success(_("successfully_added"));
    if (props.setSideBar) {
      props.setSideBar("DEFAULT");
    }
  };

  const failSave = (e) => {
    setIsSaving(false);
    clearInterval(saveInterval);
    if (
      e?.response?.data?.errors?.length > 0 &&
      e.response.data.errors[0].message
    ) {
      toast.error(_(e.response.data.errors[0].message));
    } else {
      toast.error(_("error"));
    }
  };

  const createContactFolderRole = (folderUuid) => {
    let contactData = {
      email: data.contactEmail,
      first_name: data.contactFirstName,
      last_name: data.contactLastName,
      role_type: "LEGAL_REPRESENTATIVE",
      telephone: data.contactPhone,
      contact_id: data.contactId,
    };
    if (data.contactEmail) {
      if (data.contactId) {
        saveGoPeopleFolderContact(goPeopleCommunityId, folderUuid, [contactData])
          .then(() => finishSave())
          .catch(failSave);
      } else {
        let contactPerData = {
          emails: [data.contactEmail],
          first_name: data.contactFirstName,
          last_name: data.contactLastName,
          community_id: goPeopleCommunityId,
          phones: data.contactPhone ? [data.contactPhone] : [],
          language: data.languagePreferences.map((lng) => lng.value)[0],
        };
        saveGoPeopleContact(goPeopleCommunityId, contactPerData)
          .then((contactResponse) => {
            contactData = {
              ...contactData,
              contact_id: contactResponse.uuid,
            };
            return saveGoPeopleFolderContact(goPeopleCommunityId, folderUuid, [
              contactData,
            ]).then(() => finishSave());
          })
          .catch(failSave);
      }
    } else {
      // The folder is already created at this point. This branch had no completion at
      // all, so the button stayed on its loader, the panel never closed and the list
      // was only refreshed by a manual page reload. Creation now requires a linked
      // contact, so this is a safety net rather than the normal path.
      finishSave();
    }
  };

  const handleUpdate = async () => {
    if (!isFetchingEmail && !isSaving) {
      let clientData = {
        uen: data.uen,
        organization_id: goPeopleOrganizationId,
        community_id: goPeopleCommunityId,
      };
      if (data.name !== props.currentItem.folder_name) {
        clientData = {
          ...clientData,
          folder_name: data.name,
        };
      }
      if (data.legalForm.value !== props.currentItem.legal_form) {
        clientData = {
          ...clientData,
          legal_form: data.legalForm.value,
        };
      }

      if (
        data.languagePreferences.filter((value) =>
          props.currentItem.languages?.includes(value.value)
        ).length === data.languagePreferences.length &&
        data.languagePreferences.length === props.languages
      ) {
        clientData = {
          ...clientData,
          languages: data.languagePreferences.map((lng) => lng.value),
        };
      }
      let legalRepresentative = props.currentItem.contacts.filter(
        (c) => c.role_type === "LEGAL_REPRESENTATIVE"
      )[0];
      let promises = [];
      if (data.contactEmail !== legalRepresentative?.emails[0]) {
        let contactRoleData = {
          email: data.contactEmail,
          first_name: data.contactFirstName,
          last_name: data.contactLastName,
          role_type: "LEGAL_REPRESENTATIVE",
          telephone: data.contactPhone,
        };
        if (!data.contactId) {
          let contactData = {
            first_name: data.contactFirstName,
            last_name: data.contactLastName,
            emails: [data.contactEmail],
          };
          if (data.contactPhone && data.contactPhone !== "") {
            contactData = {
              ...contactData,
              phones: [data.contactPhone],
            };
          }
          saveGoPeopleContact(goPeopleCommunityId, contactData).then(
            (contactResponse) => {
              contactRoleData = {
                ...contactRoleData,
                contact_id: contactResponse.uuid,
              };
              let contactPromise = saveGoPeopleFolderContact(
                goPeopleCommunityId,
                props.currentItem.uuid,
                [contactRoleData]
              );
              promises += [contactPromise];
            }
          );
        } else {
          let contactPromise = saveGoPeopleFolderContact(
            goPeopleCommunityId,
            props.currentItem.uuid,
            [contactRoleData]
          );
          promises += [contactPromise];
        }
      }
      if (Object.keys(clientData).length > 1) {
        let folderPromise = updateGoPeopleClient(
          jwtToken,
          goPeopleCommunityId,
          props.currentItem.uuid,
          clientData
        );
        promises += [folderPromise];
      }
      const response = await Promise.all(promises)
        .then((values) => {
          setIsSaving(false);
          props.afterSave();
          setData(initData);
          props.setCurrentItem(null);
          toast.success(_("successfully_added"));
          clearInterval(saveInterval);
          if (props.setSideBar) {
            props.setSideBar("DEFAULT");
          }
        })
        .catch((e) => {
          setIsSaving(false);
          clearInterval(saveInterval);
          showApiError(e);
        });
    }
  };

  const showApiError = (e) => {
    const { fieldErrors, message } = parseApiError(e);
    const mapped = {};

    Object.keys(fieldErrors).forEach((key) => {
      if (CONTACT_FIELD_KEYS[key]) {
        mapped[CONTACT_FIELD_KEYS[key]] = fieldErrors[key];
      }
    });

    if (Object.keys(mapped).length === 0) {
      // Nothing to highlight here, so do not send the user looking for a field.
      toast.error(_("error"));
      return;
    }

    setErrors((prevErrors) => ({ ...prevErrors, ...mapped }));
    toast.error(message);
  };

  const handleSave = () => {
    if (!isFetchingEmail && !isSaving) {
      // Nothing is sent while a required field is missing, whatever enabled the button.
      if (validate()) {
        return;
      }

      let clientData = {
        uen: data.uen,
        folder_name: data.name,
        legal_form: data.legalForm.value,
        languages: data.languagePreferences.map((lng) => lng.value),
      };
      clearInterval(saveInterval);
      setIsSaving(true);

      if (fetchedFolder) {
        createContactFolderRole(fetchedFolder.uuid);
      } else {
        saveGoPeopleClient(
          jwtToken,
          goPeopleCommunityId,
          goPeopleOrganizationId,
          clientData
        )
          .then((folderResponse) => {
            createContactFolderRole(folderResponse.uuid);
          })
          .catch((e) => {
            setIsSaving(false);
            clearInterval(saveInterval);
            showApiError(e);
          });
      }
    }
  };

  const save = () => {
    setIsSaving(true);
    if (props.currentItem?.uuid) {
      saveInterval = setInterval(handleUpdate, 800);
    } else {
      saveInterval = setInterval(handleSave, 800);
    }
  };

  const handleUenKeyUp = (name, value) => {
    let error = "";
    if (!validateUen(value)) error = _("validate_uen");
    else error = "";
    if (value.length === 0) error = _("required_field");
    setErrors({ ...errors, [name]: error });
  };

  const handleConfirmFolderByUen = (folder = null) => {
    if (!folder) {
      folder = fetchedFolder;
    }
    let folderData = {
      id: folder.id,
      uen: folder.uen,
      name: folder.name,
    };
    if (folder.legalForm) {
      folderData.legalForm = {
        value: folder.legalForm,
        label: LEGAL_FORM[folder.legalForm],
      };
    }
    if (folder.legalRepresentative) {
      let emailResult = folder.legalRepresentative.email.filter(
        (i) => i.email === folder.legalRepresentative.mainEmail
      );
      let phoneResult = folder.legalRepresentative.phone.filter(
        (i) => i.number === folder.legalRepresentative.mainPhone
      );

      folderData = {
        ...folderData,
        contactId: folder.legalRepresentative.id,
        contactEmailId: emailResult[0].id,
        contactEmail: folder.legalRepresentative.mainEmail,
        contactEmailValue: obscureEmail(folder.legalRepresentative.mainEmail),
        contactFirstName: folder.legalRepresentative.firstName,
        contactLastName: folder.legalRepresentative.lastName,
        contactPhone: folder.legalRepresentative.mainPhone,
        contactPhoneValue: formatHidePhone(
          folder.legalRepresentative.mainPhone
        ),
        contactPhoneId:
          phoneResult && phoneResult.length > 0 ? phoneResult[0].id : "",
      };
    }
    setData({
      ...data,
      ...folderData,
    });
    setErrors({ ...errors, name: "" });
    setDisableFields(true);
    setShowConfirmFolder(false);
  };

  const handleUenBlur = (value) => {
    if (validateUen(value)) {
      setErrors({ ...errors, uen: "" });
      setIsFetchingCompany(true);
      getGoPeopleFoldersByUen(
        jwtToken,
        goPeopleCommunityId,
        value.replace(/\.|\s|BE|be/g, "")
      )
        .then((response) => {
          if (response.folder_count > 0) {
            setFetchedFolder(response.folders[0]);
            handleConfirmFolderByUen(response.folders[0]);
            let languagePreferences = [];
            if (
              response.folders[0].languages &&
              response.folders[0].languages.length > 0
            ) {
              response.folders[0].languages.forEach((lng) => {
                LANGUAGES.forEach((i) => {
                  if (i.value === lng) {
                    languagePreferences.push(i);
                  }
                });
              });
            }
            let legalRepresentativeRole = response.contactsFolderRole?.filter(
              (cont) =>
                cont.role_type === "LEGAL_REPRESENTATIVE" &&
                cont.folder_id === response.folders[0].uuid
            );
            let legalRepresentative =
              legalRepresentativeRole && legalRepresentativeRole.length
                ? response.contacts.filter(
                    (contact) =>
                      contact.uuid === legalRepresentativeRole[0].contact_id
                  )[0]
                : null;
            setData({
              ...data,
              uen: response.folders[0].uen,
              name: response.folders[0].folder_name,
              legalForm: {
                value: response.folders[0].legal_form,
                label: LEGAL_FORM[response.folders[0].legal_form],
              },
              languagePreferences: languagePreferences
                ? languagePreferences
                : "",
              contactId: legalRepresentative?.uuid,
              contactEmail: legalRepresentative?.emails?.length
                ? legalRepresentative?.emails[0]
                : "",
              contactEmailValue: legalRepresentative?.emails?.length
                ? legalRepresentative?.emails[0]
                : "",
              contactFirstName: legalRepresentative
                ? legalRepresentative.first_name
                : "",
              contactLastName: legalRepresentative
                ? legalRepresentative.last_name
                : "",
              contactPhone: legalRepresentative?.phones?.length
                ? legalRepresentative?.phones[0]
                : "",
              contactPhoneValue: legalRepresentative?.phones?.length
                ? legalRepresentative?.phones[0]
                : "",
            });
            if (legalRepresentative) {
              setDisableContactFields(true);
            }
            setErrors({ ...errors, name: "" });
          } else {
            setDisableFields(false);
            setDisableContactFields(false);
            setData({ ...initData, uen: value });
          }
        })
        .catch((e) => {
          if (fetchedFolder) {
            setData({
              ...data,
              id: "",
              name: "",
              legalForm: { value: "SRL", label: LEGAL_FORM.SRL },
              languagePreferences: [LANGUAGES[0]],
              contactId: "",
              contactEmailId: "",
              contactEmail: "",
              contactEmailValue: "",
              contactFirstName: "",
              contactLastName: "",
              contactPhone: "",
              contactPhoneId: "",
              contactPhoneValue: "",
            });
          }
          setFetchedFolder(null);
          if (e.response && e.response.status === 404) {
            getGoPeopleFoldersByUen(jwtToken, goPeopleCommunityId, value)
              .then((res) => {
                const resp = res.data.data;
                if (resp.isValid) {
                  setData({
                    ...data,
                    name: resp.name,
                  });
                  setErrors({ ...errors, name: "" });
                }
                // else {
                //   setData({ ...data, name: "" });
                // }
              })
              .finally(() => {
                setIsFetchingCompany(false);
                setDisableFields(false);
              });
          }
        })
        .finally(() => {
          setIsFetchingCompany(false);
        });
    } else {
      if (value.length === 0)
        setErrors({ ...errors, uen: _("required_field") });
      else setErrors({ ...errors, uen: _("validate_uen") });
    }
  };

  const handlePhoneBlur = (name, value) => {
    // Delegates to getFieldError so the blur and the keystroke cannot disagree:
    // this used to demand 6 characters here and 10 there, with "required field"
    // as the message on an optional field.
    const error = getFieldError(name, value);
    setErrors({ ...errors, [name]: error });
    if (!error) {
      // formatPhone only inserts spaces and parentheses, which PHONE_REGEX allows,
      // so the value it produces still passes the rule applied above.
      setData({ ...data, contactPhone: formatPhone(value) });
    }
  };

  const handleBlur = (name, value) => {
    if (value.length === 0)
      setErrors({ ...errors, [name]: _("required_field") });
    else setErrors({ ...errors, [name]: "" });
  };

  const handleLanguageChange = (option) => {
    if (option.length === 0)
      setErrors({ ...errors, languagePreferences: _("required_field") });
    else setErrors({ ...errors, languagePreferences: "" });
    setData({ ...data, languagePreferences: option });
  };

  const handleChange = (name, value) => {
    if (name === "contactPhone") {
      setData({ ...data, [name]: value, contactPhoneValue: value });
    } else {
      if (value.length === 0) {
        setErrors({ ...errors, [name]: _("required_field") });
      } else {
        setErrors({ ...errors, [name]: "" });
      }
      setData({ ...data, [name]: value });
    }
  };

  const handleEmailBlur = async (e) => {
    if (data.contactEmail.length > 0 && validateEmail(data.contactEmail)) {
      setIsFetchingUser(true);
      isFetchingEmail = true;
      searchGoPeopleCommunityByEntity(
        jwtToken,
        goPeopleCommunityId,
        "contact",
        data.contactEmail,
        0,
        "emails",
        true
      )
        .then((resp) => {
          if (resp.total > 0) {
            setDisableContactFields(true);
            setData({
              ...data,
              contactFirstName: resp.results[0].firstname,
              contactLastName: resp.results[0].lastname,
              contactPhone: resp.results[0].phones?.length
                ? resp.results[0].phones[0]
                : "",
              contactPhoneValue: resp.results[0].phones?.length
                ? formatHidePhone(resp.results[0].phones[0])
                : "",
              contactId: resp.results[0].uuid,
            });
          } else {
            setDisableContactFields(false);
            setData({
              ...data,
              contactFirstName: "",
              contactLastName: "",
              contactPhone: "",
              contactPhoneValue: "",
              contactId: null,
            });
          }
          setIsFetchingUser(false);
          isFetchingEmail = false;
        })
        .catch((e) => {
          setIsFetchingUser(false);
          setDisableContactFields(false);
          isFetchingEmail = false;
          setData({ ...data, contactEmailId: "", contactPhoneId: "" });
        });
    }
  };

  return (
    <div className={props.addFormClass ? styles.form : ""}>
      <div
        className={`${styles.form_content} ${
          props.addFormClass && styles.form_content_pad
        } ${isFetchingUser && styles.loading}`}
        style={{ position: "relative" }}
      >
        {isFetchingCompany && <div className="lmask"></div>}
        {/* Was hardcoded in French, so it neither reflected the edit mode nor
            followed the interface language. isCreation already tells the two
            modes apart for the submit rules. */}
        <div className={styles.titles}>
          <h4>{isCreation ? _("manual_client_title") : _("edit_client_title")}</h4>
          <p>
            {isCreation
              ? _("manual_client_subtitle")
              : _("edit_client_subtitle")}
          </p>
        </div>
        <FormInput
          name="uen"
          required={true}
          info={_("uen_info")}
          label={_("business_number")}
          autoComplete="none"
          error={errors.uen}
          value={data.uen}
          placeholder="BE XXXX.XXX.XXX"
          onKeyUp={(e) => handleUenKeyUp(e.target.name, e.target.value)}
          handleBlur={(e) => handleUenBlur(e.target.value)}
          onChange={(e) => handleChange(e.target.name, e.target.value)}
        />

        <FormInput
          name="name"
          value={data.name}
          required={true}
          label={_("Nom d’entreprise")}
          error={errors.name}
          autoComplete="none"
          handleBlur={(e) => handleBlur(e.target.name, e.target.value)}
          onChange={(e) => handleChange(e.target.name, e.target.value)}
          disabled={disableFields}
        ></FormInput>

        <div className="ttp-form-group">
          <label className="ttp-label">{_("legal_form")}</label>
          <Select
            styles={SELECT_STYLES}
            options={Object.entries(LEGAL_FORM).map(([k, v]) => {
              return { value: k, label: v };
            })}
            isSearchable={false}
            required={true}
            value={data.legalForm}
            onChange={(e) => setData({ ...data, legalForm: e })}
            isDisabled={disableFields}
          />
        </div>

        <div className="ttp-form-group">
          <label className="ttp-label">{_("language")}</label>
          <Select
            isMulti
            styles={SELECT_STYLES}
            options={LANGUAGES}
            isSearchable={false}
            required={true}
            value={data.languagePreferences}
            onChange={(e) => handleLanguageChange(e)}
            isDisabled={disableFields}
          />
          {errors.languagePreferences && (
            <div className="ttp-error">{errors.languagePreferences}</div>
          )}
        </div>

        <div className={styles.linkedContact}>{_("linked_contact")}</div>
        <FormInput
          name="contactEmail"
          required={true}
          label={_("email_address")}
          autoComplete="none"
          error={errors.contactEmail}
          value={data.contactEmailValue}
          handleBlur={(e) => handleEmailBlur(e)}
          onKeyUp={(e) => handleKeyUp(e.target.name, e.target.value)}
          onChange={(e) => handleEmailChange(e.target.name, e.target.value)}
          disabled={disableContactFields}
        />
        <div className="grid-x grid-margin-x">
          <div className="cell small-12 medium-6">
            <FormInput
              name="contactLastName"
              required={true}
              label={_("lastname")}
              autoComplete="none"
              error={errors.contactLastName}
              value={data.contactLastName}
              handleBlur={(e) => handleBlur(e.target.name, e.target.value)}
              onKeyUp={(e) => handleKeyUp(e.target.name, e.target.value)}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              disabled={disableContactFields}
            />
          </div>
          <div className="cell small-12 medium-6">
            <FormInput
              name="contactFirstName"
              required={true}
              label={_("firstname")}
              autoComplete="none"
              error={errors.contactFirstName}
              value={data.contactFirstName}
              handleBlur={(e) => handleBlur(e.target.name, e.target.value)}
              onKeyUp={(e) => handleKeyUp(e.target.name, e.target.value)}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              disabled={disableContactFields}
            />
          </div>
        </div>
        <FormInput
          name="contactPhone"
          // required={true}
          label={_("phone_number")}
          placeholder="+32XXXXXXXX"
          autoComplete="none"
          value={data.contactPhoneValue}
          error={errors.contactPhone}
          onKeyUp={(e) => handleKeyUp(e.target.name, e.target.value)}
          onChange={(e) => handleChange(e.target.name, e.target.value)}
          handleBlur={(e) => handlePhoneBlur(e.target.name, e.target.value)}
          disabled={disableContactFields}
        />
      </div>
      <div className={styles.actions}>
        <Button
          variant="default"
          onClick={(e) => {
            setData(initData);
            setErrors(initErrors);
            if (props.setSideBar) props.setSideBar("DEFAULT");
          }}
        >
          {_("cancel")}
        </Button>
        {isSaving ? (
          <Button
            variant="primary"
            style={{ paddingTop: "15px", paddingBottom: "15px" }}
          >
            <Loader
              style={{
                height: "10px",
              }}
              color={"#fff"}
            />
          </Button>
        ) : (
          <Button disabled={!isValidForm} onClick={() => save()}>
            {props.currentItem ? _("edit") : _("add")}
          </Button>
        )}
      </div>

      <ModalConfirm
        type="publish"
        isOpen={showConfirmFolder}
        onCancel={() => {
          setData(initData);
          setShowConfirmFolder(false);
          setDisableFields(false);
        }}
        onConfirm={() => handleConfirmFolderByUen()}
        title={_("add_confirm")}
        text={_("text_add_exist_client")}
        labelNo={_("no")}
        labelYes={_("yes")}
        labelError={_("error")}
      />
    </div>
  );
};

export default ClientForm;
